<template lang="pug">
.today-availability-container
  .select-container
    aiq-select(placeholder="Select Teams"
              v-model="selectedTeams"
              @change="handleTeamsSelectionChange"
              multiple
              size="small"
              popper-class="custom-header"
              :reserve-keyword="false")
      aiq-option(v-for="team in teamsList"
                :key="team"
                :label="team"
                :value="team")
    aiq-select(placeholder="Select Agents"
              v-model="selectedAgents"
              @change="handleAgentsSelectionChange"
              value-key='agent_id'
              multiple
              size="small"
              popper-class="custom-header"
              :reserve-keyword="false")
      aiq-option(v-for="agent in agentsList"
                :key="agent.agent_id"
                :label="agent.label"
                :value="agent")
  .timelines(v-if="chartData.length")
    .title {{ chartConfig.title }}
    overview-chart(:keepChartOnly="true"
                  :chartConfig="chartConfig"
                  :preLoadedchartData="chartData"
                  visualization='aiq-timeline-chart')
  .no-chart(v-else)
    span {{ $t('pulse_tab.today_tab.availability_tab.no_data') }}
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import get from 'lodash/get';
import truncate from 'lodash/truncate';
import OverviewChart from '@/components/Chart.vue';
import { FIXED_AGENT_STATUS } from '@/constants';
import { increaseOpacity } from '@/libs';

const MAX_AGENT_NAME_LENGTH = 35;

export default {
  name: 'PulseTodayAvailability',
  components: { OverviewChart },
  computed: {
    ...mapGetters({
      allTeams: 'teams/sortedTeams',
      teamAgents: 'agentsDashboard/agents',
    }),
    ...mapState({
      agentStatuses: state => state.settings.agentStatuses,
    }),
    agentStatusDataset() {
      const metricsDataset = get(this.metrics, 'data.dataset', []);
      const agentStatusMetrics = metricsDataset.find(d => d.metric === 'agent_status');
      return get(agentStatusMetrics, 'dataset', []);
    },
    displayedAgents() {
      if (!this.selectedTeams.length) {
        return [];
      }
      const agentStatusDataset = this.getAgentStatusDataset(this.metrics);
      const dataSetIds = new Set(agentStatusDataset.map(agent => agent.agent_id));
      return this.teamAgents.filter(agent => dataSetIds.has(agent.id));
    },
    scaleMapping() {
      const domain = [];
      const range = [];
      for (const agentStatus of this.agentStatuses.statuses) {
        const statusColor = FIXED_AGENT_STATUS[agentStatus.status].color;
        const subStatusesLength = agentStatus.subStatuses.length;
        const opacityStep = subStatusesLength ? 1 / (subStatusesLength + 1) : 0;
        domain.push(agentStatus.label);
        range.push(statusColor);
        agentStatus.subStatuses.forEach((subStatus, idx) => {
          domain.push(`${agentStatus.label} - ${subStatus.label}`);
          range.push(increaseOpacity(statusColor, opacityStep * (idx + 1)));
        });
      }
      return { domain, range };
    },
    chartConfig() {
      return {
        title: 'Agent Status | Sub-status by Hour',
        name: 'agent_status',
        metric: 'timeslice',
        graphParams: {
          domain: this.scaleMapping.domain,
          range: this.scaleMapping.range,
          colorProperty: 'fullStatus',
          timeRange: {
            beginning: new Date().setHours(0, 0, 0, 0),
            ending: new Date().setHours(23, 59, 0, 0), // eslint-disable-line
          },
          layout: {
            row: {
              height: 20,
              margin: 5,
            },
            axis: {
              height: 20,
            },
            chartMargins: {
              top: 30,
              bottom: 30,
              right: 30,
              left: 220,
            },
          },
        },
      };
    },
  },
  props: {
    metrics: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      chartData: [],
      teamsList: [],
      selectedTeams: [],
      agentsList: [],
      selectedAgents: [],
    };
  },
  mounted() {
    if (this.metrics.lastFetchedAt) {
      this.handleTeamsListChange();
      this.prepareChartData();
    }
  },
  methods: {
    getAgentStatusDataset(metrics) {
      const metricsDataset = get(metrics, 'data.dataset', []);
      const agentStatusMetrics = metricsDataset.find(d => d.metric === 'agent_status');
      return get(agentStatusMetrics, 'dataset', []);
    },
    getSelectedAgentDataPoints(allAgentsDataset) {
      if (this.selectedAgents.length) {
        const selectedAgentIds = new Set(this.selectedAgents.map(agent => agent.id));
        return allAgentsDataset.filter(dataPoint => selectedAgentIds.has(dataPoint.agent_id));
      }
      const selectedTeamNames = new Set(this.selectedTeams.map(team => team.name));
      return allAgentsDataset.filter(dataPoint => dataPoint.teams.some(teamName => selectedTeamNames.has(teamName)));
    },
    getFullStatus(status, subStatus) {
      let fullStatus = '';
      for (const agentStatus of this.agentStatuses.statuses) {
        if (status === agentStatus.status) {
          fullStatus = agentStatus.label;
          if (subStatus) {
            const agentSubstatus = agentStatus.subStatuses.find(s => s.status === subStatus);
            fullStatus += ` - ${agentSubstatus.label}`;
          }
        }
      }
      return fullStatus;
    },
    prepareChartData() {
      const dataset = this.selectedAgents.length ? this.selectedAgents : this.agentsList;
      this.chartData = dataset.map(agent => {
        const times = agent.data_points
          .sort((a, b) => new Date(a.ts) - new Date(b.ts))
          .map((dataPoint, i, allDataPoints) => ({
            starting_time: new Date(dataPoint.ts),
            ending_time: allDataPoints[i + 1] ? new Date(allDataPoints[i + 1].ts) : new Date(),
            fullStatus: this.getFullStatus(dataPoint.status, dataPoint.substatus),
          }));

        return {
          label: truncate(agent.label, { length: MAX_AGENT_NAME_LENGTH }),
          tooltipLabel: agent.label,
          className: agent.agent_id,
          times,
          tooltipData: this.getTooltipData(times),
        };
      });
    },
    getTooltipData(times) {
      if (!times.length) {
        return;
      }

      const tooltipData = { items: [] };
      const timeAggregates = {};
      let totalTimeSeconds = 0;
      const { domain, range } = this.scaleMapping;
      for (const slot of times) {
        const { fullStatus, starting_time, ending_time } = slot;
        const durationSeconds = (ending_time - starting_time) / 1000; // eslint-disable-line
        timeAggregates[fullStatus] = timeAggregates[fullStatus] ? timeAggregates[fullStatus] + durationSeconds : durationSeconds;
      }
      for (let i = 0 ; i < domain.length ; i++) {
        if (timeAggregates[domain[i]]) {
          const durationSeconds = timeAggregates[domain[i]];
          totalTimeSeconds += durationSeconds;
          tooltipData.items.push({
            status: domain[i],
            color: range[i],
            duration: this.$aiq.formatters.timeDelta(durationSeconds),
          });
        }
      }
      tooltipData.footer = { label: 'Total', totalTime: this.$aiq.formatters.timeDelta(totalTimeSeconds) };
      return tooltipData;
    },
    handleAgentsSelectionChange() {
      this.prepareChartData();
    },
    handleTeamsSelectionChange(selectedTeams) {
      this.updateAgentsList(selectedTeams);
      this.prepareChartData();
    },
    handleTeamsListChange() {
      // update teams list
      const teamsSet = new Set();
      this.agentStatusDataset.forEach(agent => {
        agent.teams.forEach(team => teamsSet.add(team));
      });
      this.teamsList = Array.from(teamsSet);

      // update selected teams
      const updatedSelectedTeams = this.selectedTeams.filter(t => teamsSet.has(t));
      this.selectedTeams = updatedSelectedTeams;

      // update agents list
      this.updateAgentsList(updatedSelectedTeams);
    },
    updateAgentsList(selectedTeams) {
      let updatedAgentsList = [];
      if (!selectedTeams.length) {
        // don't filter agents list
        updatedAgentsList = this.agentStatusDataset;
      } else {
        // filter agents list by selected teams
        const selectedTeamsSet = new Set(selectedTeams);
        updatedAgentsList = this.agentStatusDataset.filter(dataPoint => dataPoint.teams.some(teamName => selectedTeamsSet.has(teamName)));
      }
      // update selected agents
      this.selectedAgents = updatedAgentsList.filter(listAgent =>
        this.selectedAgents.some(selectedAgent => selectedAgent.agent_id === listAgent.agent_id),
      );
      // set agents list
      this.agentsList = updatedAgentsList;
    },
  },
  watch: {
    metrics: {
      handler() {
        this.handleTeamsListChange();
        this.prepareChartData();
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/aiq-mixins.scss";
@import "@/styles/aiq-variables.scss";

.today-availability-container {
  .select-container {
    display: flex;
    column-gap: 20px;
  }

  .timelines {
    @include scrollable(calc(100vh - 270px));
    display: flex;
    flex-direction: column;
    margin-top: 10px;

    .title {
      background: #fff;
      text-align: center;
      padding-top: 10px;
      color: #000;
    }
  }

  .no-chart {
    height: calc(100vh - 270px);
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>